import { motion } from "framer-motion";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const faqCategories = [
  {
    title: "General",
    faqs: [
      {
        q: "What services does Dhivisha Architects offer?",
        a: "We offer comprehensive architectural and interior design services including residential design, commercial projects, institutional buildings, industrial structures, cultural & religious architecture, renovation & remodelling, landscape design, and art décor & lighting solutions.",
      },
      {
        q: "Where is Dhivisha Architects located?",
        a: "We are based in Kumbakonam, Tamil Nadu, and serve clients across Tamil Nadu and South India. We also take up projects in other regions on a case-by-case basis.",
      },
      {
        q: "How many projects has Dhivisha Architects completed?",
        a: "We have successfully completed over 350 projects across residential, commercial, cultural, and industrial categories, serving 300+ happy clients.",
      },
    ],
  },
  {
    title: "Process & Timeline",
    faqs: [
      {
        q: "What is the typical process for starting a project?",
        a: "Our process begins with an initial consultation to understand your vision and requirements. We then move through concept design, detailed drawings, approvals, and finally construction supervision. Each phase involves client feedback to ensure alignment.",
      },
      {
        q: "How long does a residential project usually take?",
        a: "A typical residential project takes 12–18 months from concept to completion, depending on the scale and complexity. The design phase usually takes 4–6 weeks, followed by approvals and construction.",
      },
      {
        q: "Do you handle government approvals and permits?",
        a: "Yes, we assist with all necessary building approvals, municipal permits, and statutory clearances required for your project.",
      },
    ],
  },
  {
    title: "Cost & Budget",
    faqs: [
      {
        q: "How much does it cost to hire an architect?",
        a: "Our fees depend on the project scope, size, and complexity. We offer flexible fee structures including percentage-based, lump-sum, and phased payment options. Contact us for a personalised quote.",
      },
      {
        q: "Can you work within a specific budget?",
        a: "Absolutely. We specialise in value engineering — optimising designs to deliver the best quality within your budget without compromising aesthetics or structural integrity.",
      },
      {
        q: "Are interior design services included in the architecture fee?",
        a: "Interior design is typically quoted separately as it involves different materials and detailing. However, we offer bundled packages for clients who want end-to-end design solutions.",
      },
    ],
  },
  {
    title: "Design & Construction",
    faqs: [
      {
        q: "Do you provide 3D visualisations before construction?",
        a: "Yes, we provide detailed 3D renderings and walkthroughs so you can visualise the final outcome before construction begins. This helps in making informed design decisions.",
      },
      {
        q: "Do you supervise the construction process?",
        a: "Yes, we offer site supervision services to ensure that the construction adheres to the approved design, quality standards, and timeline.",
      },
      {
        q: "Can you renovate or remodel an existing building?",
        a: "Yes, renovation and remodelling is one of our core services. We can transform existing spaces with modern designs while respecting the original structure.",
      },
      {
        q: "Do you use sustainable or green building practices?",
        a: "We are committed to sustainable architecture. We incorporate energy-efficient designs, natural ventilation, rainwater harvesting, and eco-friendly materials wherever possible.",
      },
    ],
  },
];

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqCategories.flatMap((cat) =>
    cat.faqs.map((faq) => ({
      "@type": "Question",
      name: faq.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.a,
      },
    }))
  ),
};

const FAQ = () => {
  return (
    <Layout>
      <SEOHead
        title="FAQ — Registered Architect in Kumbakonam & Delta District"
        description="Frequently asked questions about Dhivisha Architects, COA-registered firm serving Kumbakonam & Cauvery Delta districts: services, process, pricing & construction."
        path="/faq"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
      />

      {/* Hero */}
      <section className="py-16 sm:py-24 bg-secondary">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-secondary-foreground mb-4"
          >
            Frequently Asked Questions
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="text-muted-foreground text-sm sm:text-base max-w-xl mx-auto"
          >
            Everything you need to know about working with Dhivisha Architects.
          </motion.p>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-12 sm:py-20">
        <div className="max-w-3xl mx-auto px-4 space-y-10 sm:space-y-14">
          {faqCategories.map((category, catIdx) => (
            <motion.div
              key={category.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: catIdx * 0.08 }}
            >
              <h2 className="text-xl sm:text-2xl font-bold text-foreground mb-4 sm:mb-6">
                {category.title}
              </h2>
              <Accordion type="single" collapsible className="space-y-2">
                {category.faqs.map((faq, i) => (
                  <AccordionItem
                    key={i}
                    value={`${catIdx}-${i}`}
                    className="border border-border rounded px-4 sm:px-5"
                  >
                    <AccordionTrigger className="text-sm sm:text-base font-medium text-foreground/90 text-left py-4">
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-muted-foreground pb-4 leading-relaxed">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </motion.div>
          ))}
        </div>
      </section>
    </Layout>
  );
};

export default FAQ;
